import { api } from '@/lib/axios';

export enum ProcessFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
}

export interface ProcessTask {
  id: string;
  processId: string;
  name: string;
  description?: string;
  order: number;
  minTime: string;
  maxTime: string;
  photoRequired: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Process {
  id: string;
  name: string;
  description?: string;
  frequency: ProcessFrequency;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  tasks: ProcessTask[];
  _count?: {
    executions: number;
  };
}

export interface CreateProcessTaskDto {
  name: string;
  description?: string;
  order: number;
  minTime: string;
  maxTime: string;
  photoRequired: boolean;
}

export interface CreateProcessDto {
  name: string;
  description?: string;
  frequency: ProcessFrequency;
  tasks: CreateProcessTaskDto[];
}

export interface UpdateProcessDto {
  name?: string;
  description?: string;
  frequency?: ProcessFrequency;
  active?: boolean;
}

export interface UpdateProcessTaskDto {
  name?: string;
  description?: string;
  order?: number;
  minTime?: string;
  maxTime?: string;
  photoRequired?: boolean;
  active?: boolean;
}

class ProcessesService {
  async getAll(params?: { active?: boolean; frequency?: string }): Promise<Process[]> {
    const { data } = await api.get('/processes', { params });
    return data;
  }

  async getById(id: string): Promise<Process> {
    const { data } = await api.get(`/processes/${id}`);
    return data;
  }

  async create(process: CreateProcessDto): Promise<Process> {
    const { data } = await api.post('/processes', process);
    return data;
  }

  async update(id: string, process: UpdateProcessDto): Promise<Process> {
    const { data } = await api.patch(`/processes/${id}`, process);
    return data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/processes/${id}`);
  }

  async addTask(processId: string, task: CreateProcessTaskDto): Promise<ProcessTask> {
    const { data } = await api.post(`/processes/${processId}/tasks`, task);
    return data;
  }

  async updateTask(taskId: string, task: UpdateProcessTaskDto): Promise<ProcessTask> {
    const { data } = await api.patch(`/processes/tasks/${taskId}`, task);
    return data;
  }

  async deleteTask(taskId: string): Promise<void> {
    await api.delete(`/processes/tasks/${taskId}`);
  }
}

export const processesService = new ProcessesService();
